import Debounce from '@sd-utility/debounce';
import { namedUuid } from '@tvg/fuse-utils';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import NextButton from './next-button';
import PreviousButton from './previous-button';
import {
	StyledTabList,
	StyledTabListContainer,
	StyledTabPanelContainer,
	StyledTabs,
} from './styled-tabs';
import Tab from './tab';
import TabPanel from './tab-panel';

const { useState, useEffect, useRef, useMemo } = React;

const defaultProps = {
	className: undefined,
	dataAnalyticsId: undefined,
	dataTestId: 'fuse-tabs-test-id',
	landOnTab: undefined,
	tabChangeCallback: () => {},
};

const propTypes = {
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
	className: PropTypes.string,
	dataAnalyticsId: PropTypes.string,
	dataTestId: PropTypes.string,
	landOnTab: PropTypes.number,
	tabChangeCallback: PropTypes.func,
	tabListAriaLabel: PropTypes.string.isRequired,
};

function isDevice() {
	if(typeof window === 'undefined') return false;

	let isResizable = false;
	const userAgent = window?.navigator?.userAgent;

	if (userAgent.match(/Android/i)) {
		isResizable = true;
	} else if (userAgent.match(/iPhone|iPad|iPod/i)) {
		isResizable = true;
	} else if (userAgent.match(/Windows Phone/i)) {
		isResizable = true;
	}
	return isResizable;
}

function Tabs({
	landOnTab,
	children,
	className,
	dataTestId,
	dataAnalyticsId,
	tabChangeCallback,
	tabListAriaLabel,
	...props
}) {
	const [activeTab, setActiveTab] = useState(landOnTab ? landOnTab - 1 : 0);
	const [isOverflow, setIsOverflow] = useState(false);
	const [showPreviousButton, setShowPreviousButton] = useState(false);
	const [showNextButton, setShowNextButton] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(landOnTab ? landOnTab - 1 : 0);

	const tabId = useMemo(() => namedUuid('fuse-tabs-tab'), []);
	const tabPanelId = useMemo(() => namedUuid('fuse-tabs-tab-panel'), []);

	const tabsRef = useRef();
	const tabPanelsRef = useRef();
	const isMobileDevice = isDevice();

	/* istanbul ignore next */ /* jest cant emulate intersectionobserver */
	const visibleTabs = () => {
		if (isMobileDevice) return;
		const io = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.intersectionRatio > 0.99) {
					entry.target.classList.add('visible');
				} else {
					entry.target.classList.remove('visible');
				}
			});
		});

		const tabListElements = tabsRef.current.querySelectorAll('.fuse-tabs__tab-list > button');
		tabListElements.forEach((el) => {
			io.observe(el);
		});
	};

	/* istanbul ignore next */ /* jest cant emulate intersectionobserver */
	const tabsOverflow = () => {
		const { scrollWidth, clientWidth, scrollLeft } = tabsRef.current;

		if (clientWidth < scrollWidth) {
			setIsOverflow(true);
			setShowNextButton(true);

			if (scrollLeft === 0) {
				setShowPreviousButton(false);
			} else {
				setShowPreviousButton(true);
			}

			if(!isMobileDevice) {
				tabsRef.current
					.querySelector('.fuse-tabs__tab-list .active')
					.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
			}
			visibleTabs();
		} else {
			setIsOverflow(false);
		}
	};

	useEffect(() => {
		if (!isMobileDevice) {
			window.addEventListener('resize', tabsOverflow);
			tabsOverflow();
		}

		return () => {
			if (!isMobileDevice) {
				window.removeEventListener('resize', tabsOverflow);
			}
		};
	}, []);

	const onTabChange = useCallback(
		(e) => {
			if (typeof tabChangeCallback === 'function') tabChangeCallback(e, tabsRef);
		},
		[activeTab, tabChangeCallback],
	);

	const tabListScrollHandler = () => {
		if (tabsRef.current.scrollLeft === 0) {
			setShowPreviousButton(false);
		} else {
			setShowPreviousButton(true);
		}

		const scrollTotal = tabsRef.current.scrollWidth - tabsRef.current.offsetWidth;
		const scrollLeftPosition = tabsRef.current.scrollLeft;

		if (scrollTotal === scrollLeftPosition || scrollLeftPosition > scrollTotal) {
			setShowNextButton(false);
		} else {
			setShowNextButton(true);
		}

		visibleTabs();
	};

	const tabClickHandler = async (e, index) => {
		setActiveTab(index);
		setSelectedIndex(index);

		if (!e.target.classList.contains('visible')) {
			const scrollBehavior = isMobileDevice ? 'auto' : 'smooth';
			e.target.scrollIntoView({
				behavior: scrollBehavior,
				block: 'nearest',
				inline: 'center',
			});
		}

		/* istanbul ignore next */ /* jest cant emulate intersectionobserver */
		visibleTabs();

		tabPanelsRef.current.children[selectedIndex].focus({
			preventScroll: true,
		});
	};

	/* istanbul ignore next */ /* jest cant emulate intersectionobserver */
	const previousButtonClickHandler = () => {
		const allVisibleTabs = tabsRef.current.querySelectorAll(
			'.fuse-tabs__tab-list > button.visible',
		);

		const firstVisibleTab = allVisibleTabs[0];
		firstVisibleTab.previousElementSibling.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'end',
		});
	};

	/* istanbul ignore next */ /* jest cant emulate intersectionobserver */
	const nextButtonClickHandler = () => {
		const allVisibleTabs = tabsRef.current.querySelectorAll(
			'.fuse-tabs__tab-list > button.visible',
		);

		const lastVisibleTab = allVisibleTabs[allVisibleTabs.length - 1];
		lastVisibleTab.nextElementSibling.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'start',
		});
	};

	const setIndex = (index) => {
		const focusOnTab = tabsRef.current.children[index];

		if (focusOnTab) {
			focusOnTab.focus();
			setSelectedIndex(index);
			setActiveTab(index);
		}
	};

	const onKeyDownHandler = (e) => {
		const count = children.length;

		const keyMap = {
			ArrowRight: () => selectedIndex !== count - 1 && setIndex((selectedIndex + 1) % count),
			ArrowLeft: () => selectedIndex !== 0 && setIndex((selectedIndex - 1 + count) % count),
		};

		const action = keyMap[e.key];

		if (action) {
			e.preventDefault();
			action();
			visibleTabs();
		}
	};

	return (
		<StyledTabs className={className} data-testid={dataTestId || 'fuse-tabs-test-id'} {...props}>
			<StyledTabListContainer>
				<StyledTabList
					ref={tabsRef}
					onScroll={Debounce(() => {
						tabListScrollHandler();
					}, 200)}
					role="tablist"
					aria-orientation="horizontal"
					aria-label={tabListAriaLabel}
				>
					{children.map(({ props: { label } }, index) => (
						<Tab
							key={label}
							id={`${tabId}-${index}`}
							label={label}
							activeTab={activeTab === index}
							onClick={(e) => tabClickHandler(e, index)}
							tabIndex={selectedIndex === index ? 0 : -1}
							role="tab"
							aria-controls={`${tabPanelId}-${index}`}
							aria-selected={activeTab === index ? 'true' : 'false'}
							onKeyDown={onKeyDownHandler}
							data-analyticsid={`${dataAnalyticsId ? `${dataAnalyticsId} - ` : ''}Tab ${
								index + 1
							} - ${label}`}
							onFocus={(e) => onTabChange(e, tabsRef)}
						/>
					))}
				</StyledTabList>
				{isOverflow && (
					<>
						<PreviousButton onClick={previousButtonClickHandler} showButton={showPreviousButton} />
						<NextButton onClick={nextButtonClickHandler} showButton={showNextButton} />
					</>
				)}
			</StyledTabListContainer>
			<StyledTabPanelContainer ref={tabPanelsRef}>
				{children.map(({ props: { children: tabPanelChildren, label } }, index) => (
					<TabPanel
						key={label}
						label={label}
						activePanel={activeTab === index}
						role="tabpanel"
						aria-labelledby={`${tabId}-${index}`}
						id={`${tabPanelId}-${index}`}
						tabIndex="0"
					>
						{tabPanelChildren}
					</TabPanel>
				))}
			</StyledTabPanelContainer>
		</StyledTabs>
	);
}

Tabs.propTypes = propTypes;
Tabs.defaultProps = defaultProps;

export default Tabs;
